import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Paperclip, Lock, Flag, ChevronDown, Pencil, Trash2, Reply, Volume2, VolumeX, Users } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useChatContext } from '../../contexts/ChatContext';
import useChat from '../../hooks/useChat';
import * as chatApi from '../../api/chat';
import { setMuted, isMuted } from '../../utils/notificationSound';
import { format, isToday, isYesterday } from 'date-fns';

// Color palette for user avatars
const AVATAR_COLORS = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
function getUserColor(userId) {
  let hash = 0;
  for (let i = 0; i < (userId || '').length; i++) hash = ((hash << 5) - hash) + userId.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function UserAvatar({ user: u, size = 'sm' }) {
  const px = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-8 w-8 text-xs';
  const initials = `${(u?.first_name || u?.sender_first || '?')[0]}${(u?.last_name || u?.sender_last || '')[0]}`.toUpperCase();
  return (
    <div className={`${px} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0`}
      style={{ backgroundColor: getUserColor(u?.user_id || u?.sender_id || u?.id || '') }}>
      {initials}
    </div>
  );
}

function formatMsgTime(dateStr) {
  try {
    const d = new Date(dateStr);
    return format(d, 'h:mm a');
  } catch { return ''; }
}

function formatDateSeparator(dateStr) {
  try {
    const d = new Date(dateStr);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'MMM d, yyyy');
  } catch { return ''; }
}

// Render message content with @mention highlighting and URL linkification
function renderContent(content) {
  if (!content) return null;
  // Replace @[Name](id) with highlighted mention spans
  const parts = [];
  let last = 0;
  const mentionRe = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let m;
  while ((m = mentionRe.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(<span key={m.index} className="text-blue-600 font-medium bg-blue-50 px-0.5 rounded">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));

  return parts.map((p, i) => typeof p === 'string' ? linkify(p, i) : p);
}

function linkify(text, key) {
  const urlRe = /(https?:\/\/[^\s]+)/g;
  const segs = text.split(urlRe);
  return segs.map((seg, j) =>
    urlRe.test(seg)
      ? <a key={`${key}-${j}`} href={seg} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline break-all">{seg}</a>
      : <span key={`${key}-${j}`}>{seg}</span>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// @mention autocomplete dropdown
function MentionDropdown({ members, filter, onSelect, position }) {
  const filtered = members.filter(m => {
    const name = `${m.first_name} ${m.last_name}`.toLowerCase();
    return name.includes((filter || '').toLowerCase());
  }).slice(0, 8);

  if (filtered.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
      style={position}>
      {filtered.map(m => (
        <button key={m.user_id} onClick={() => onSelect(m)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left">
          <UserAvatar user={m} size="sm" />
          <span className="font-medium text-gray-700">{m.first_name} {m.last_name}</span>
          <span className="text-xs text-gray-400 ml-auto">{m.email}</span>
        </button>
      ))}
    </div>
  );
}

export default function RecordChatPanel({ recordId, channelId: propChannelId, onClose, referenceNumber }) {
  const { user } = useAuthStore();
  const { refreshUnread } = useChatContext();
  const [channelId, setChannelId] = useState(propChannelId || null);
  const [members, setMembers] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [recipientIds, setRecipientIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [soundMuted, setSoundMuted] = useState(isMuted());
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [privateRecipientSearch, setPrivateRecipientSearch] = useState('');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Get or create channel for this record
  useEffect(() => {
    if (propChannelId) { setChannelId(propChannelId); return; }
    if (!recordId) return;
    chatApi.getRecordChannel(recordId)
      .then(data => {
        setChannelId(data.channel.id);
        setMembers(data.members || []);
      })
      .catch(() => {});
  }, [recordId, propChannelId]);

  // Fetch members when channel changes
  useEffect(() => {
    if (!channelId) return;
    chatApi.getChannelMembers(channelId)
      .then(data => setMembers(data.members || []))
      .catch(() => {});
  }, [channelId]);

  const {
    messages, loading, hasMore, loadMore,
    sendMessage, editMessage, deleteMessage,
    emitTyping, typingUsers, uploadAttachment,
  } = useChat(channelId);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  // Detect scroll position for auto-scroll toggle
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAutoScroll(atBottom);
  }, []);

  // Handle send
  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content && attachments.length === 0) return;

    try {
      await sendMessage({
        content: content || (attachments.length > 0 ? '[Attachment]' : ''),
        visibility: isPrivate ? 'private' : 'public',
        recipientIds: isPrivate ? recipientIds : undefined,
        isUrgent,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setInputValue('');
      setAttachments([]);
      setIsUrgent(false);
      setAutoScroll(true);
      refreshUnread();
    } catch (e) {
      // Error handling via toast in the API layer
    }
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadAttachment(file);
      setAttachments(prev => [...prev, result.attachment]);
    } catch (e) {
      // ignore
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    if (!editContent.trim() || !editingId) return;
    try {
      await editMessage(editingId, editContent.trim());
      setEditingId(null);
      setEditContent('');
    } catch (e) { /* ignore */ }
  };

  // Handle @mention detection in input
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    emitTyping();

    // Check for @ mention
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([^@\s]*)$/);
    if (atMatch) {
      setMentionOpen(true);
      setMentionFilter(atMatch[1]);
    } else {
      setMentionOpen(false);
      setMentionFilter('');
    }
  };

  const handleMentionSelect = (member) => {
    const cursorPos = inputRef.current?.selectionStart || inputValue.length;
    const textBeforeCursor = inputValue.slice(0, cursorPos);
    const atPos = textBeforeCursor.lastIndexOf('@');
    const before = inputValue.slice(0, atPos);
    const after = inputValue.slice(cursorPos);
    const mention = `@[${member.first_name} ${member.last_name}](${member.user_id}) `;
    setInputValue(before + mention + after);
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  // Key handler: Enter to send, Shift+Enter for newline
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (editingId) handleEditSubmit();
      else handleSend();
    }
    if (e.key === 'Escape') {
      if (editingId) { setEditingId(null); setEditContent(''); }
      if (mentionOpen) setMentionOpen(false);
    }
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = '';
  for (const msg of messages) {
    const dateKey = new Date(msg.created_at).toDateString();
    if (dateKey !== lastDate) {
      groupedMessages.push({ type: 'separator', date: msg.created_at, key: 'sep-' + dateKey });
      lastDate = dateKey;
    }
    groupedMessages.push({ type: 'message', ...msg, key: msg.id });
  }

  const typingNames = Object.values(typingUsers);

  return (
    <div className="w-[380px] h-full border-l border-gray-200 bg-white flex flex-col shadow-lg flex-shrink-0
                     max-md:fixed max-md:inset-0 max-md:w-full max-md:z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 truncate">Case Chat</h3>
          {referenceNumber && <span className="text-xs text-gray-400 font-mono">{referenceNumber}</span>}
        </div>
        <div className="flex items-center gap-1">
          {/* Members */}
          <button onClick={() => setShowMembers(!showMembers)}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded" title="Members">
            <Users className="h-4 w-4" />
            <span className="text-[10px] ml-0.5">{members.length}</span>
          </button>
          {/* Mute toggle */}
          <button onClick={() => { const v = !soundMuted; setSoundMuted(v); setMuted(v); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded" title={soundMuted ? 'Unmute' : 'Mute'}>
            {soundMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Members dropdown */}
      {showMembers && (
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-2 max-h-40 overflow-y-auto flex-shrink-0">
          {members.map(m => (
            <div key={m.user_id} className="flex items-center gap-2 py-1">
              <UserAvatar user={m} size="sm" />
              <span className="text-xs text-gray-700">{m.first_name} {m.last_name}</span>
              <span className="text-[10px] text-gray-400 ml-auto">{m.email}</span>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
        {/* Load more */}
        {hasMore && messages.length > 0 && (
          <button onClick={loadMore} disabled={loading}
            className="w-full text-center text-xs text-blue-500 hover:text-blue-700 py-2 disabled:opacity-50">
            {loading ? 'Loading...' : 'Load older messages'}
          </button>
        )}
        {loading && messages.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-8">Loading messages...</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-8">No messages yet. Start the conversation!</div>
        )}

        {groupedMessages.map(item => {
          if (item.type === 'separator') {
            return (
              <div key={item.key} className="flex items-center gap-2 py-2">
                <div className="flex-1 border-t border-gray-100" />
                <span className="text-[10px] text-gray-400 font-medium">{formatDateSeparator(item.date)}</span>
                <div className="flex-1 border-t border-gray-100" />
              </div>
            );
          }

          const msg = item;
          const isMe = msg.sender_id === user?.id;
          const senderName = `${msg.sender_first || ''} ${msg.sender_last || ''}`.trim();
          const msgAttachments = typeof msg.attachments === 'string' ? JSON.parse(msg.attachments || '[]') : (msg.attachments || []);

          return (
            <div key={msg.key} className={`group flex gap-2 py-1 ${isMe ? 'flex-row-reverse' : ''}`}>
              {!isMe && <UserAvatar user={msg} size="sm" />}
              <div className={`max-w-[85%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                {/* Sender name + time */}
                <div className={`flex items-center gap-1.5 mb-0.5 ${isMe ? 'flex-row-reverse' : ''}`}>
                  <span className="text-[11px] font-medium text-gray-600">{isMe ? 'You' : senderName}</span>
                  <span className="text-[10px] text-gray-300">{formatMsgTime(msg.created_at)}</span>
                  {msg.visibility === 'private' && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-500">
                      <Lock className="h-2.5 w-2.5" /> Private
                    </span>
                  )}
                  {msg.is_urgent && (
                    <span className="text-[10px] px-1 py-0.5 bg-red-50 text-red-600 rounded font-medium">URGENT</span>
                  )}
                </div>

                {/* Message bubble */}
                <div className={`relative rounded-lg px-3 py-1.5 text-sm leading-relaxed break-words
                  ${isMe ? 'bg-blue-50 text-gray-800' : 'bg-gray-50 text-gray-800'}
                  ${msg.is_urgent ? 'border border-red-200' : ''}`}>

                  {/* Editing mode */}
                  {editingId === msg.id ? (
                    <div>
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full text-sm border border-gray-200 rounded p-1.5 resize-none focus:outline-none focus:border-blue-300"
                        rows={2} autoFocus />
                      <div className="flex gap-1 mt-1">
                        <button onClick={handleEditSubmit} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Save</button>
                        <button onClick={() => { setEditingId(null); setEditContent(''); }} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{renderContent(msg.content)}</div>
                  )}

                  {msg.is_edited && !editingId && (
                    <span className="text-[10px] text-gray-400 italic ml-1">(edited)</span>
                  )}

                  {/* Attachments */}
                  {msgAttachments.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {msgAttachments.map((att, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white border border-gray-100 rounded px-2 py-1.5 text-xs">
                          <Paperclip className="h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-700 truncate flex-1">{att.filename}</span>
                          <span className="text-gray-400">{formatFileSize(att.size)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Hover actions */}
                  {editingId !== msg.id && (
                    <div className={`absolute top-0 ${isMe ? 'left-0 -translate-x-full pl-1' : 'right-0 translate-x-full pr-1'}
                      opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5`}>
                      {isMe && (
                        <>
                          <button onClick={() => { setEditingId(msg.id); setEditContent(msg.content); }}
                            className="p-1 text-gray-300 hover:text-blue-500 rounded" title="Edit">
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button onClick={() => deleteMessage(msg.id)}
                            className="p-1 text-gray-300 hover:text-red-500 rounded" title="Delete">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      )}
                      {!isMe && user?.is_admin && (
                        <button onClick={() => deleteMessage(msg.id)}
                          className="p-1 text-gray-300 hover:text-red-500 rounded" title="Delete (Admin)">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing indicator */}
        {typingNames.length > 0 && (
          <div className="flex items-center gap-2 py-1 px-1">
            <div className="flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-[11px] text-gray-400">
              {typingNames.length === 1 ? `${typingNames[0]} is typing...` : `${typingNames.length} people typing...`}
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {!autoScroll && messages.length > 0 && (
        <button onClick={() => { setAutoScroll(true); messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
          className="absolute bottom-32 right-6 p-1.5 bg-white border border-gray-200 rounded-full shadow-md text-gray-400 hover:text-gray-600 z-10">
          <ChevronDown className="h-4 w-4" />
        </button>
      )}

      {/* Pending attachments */}
      {attachments.length > 0 && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((att, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2 py-1 text-xs">
                <Paperclip className="h-3 w-3 text-gray-400" />
                <span className="truncate max-w-[120px]">{att.filename}</span>
                <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  className="text-gray-300 hover:text-red-500">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Private recipients selector */}
      {isPrivate && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-amber-50 flex-shrink-0">
          <div className="text-[10px] text-amber-600 font-medium mb-1">Private: Select recipients</div>
          <div className="flex flex-wrap gap-1">
            {members.filter(m => m.user_id !== user?.id).map(m => {
              const selected = recipientIds.includes(m.user_id);
              return (
                <button key={m.user_id}
                  onClick={() => setRecipientIds(prev =>
                    selected ? prev.filter(id => id !== m.user_id) : [...prev, m.user_id]
                  )}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                    selected ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:border-amber-300'
                  }`}>
                  {m.first_name} {m.last_name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 px-3 py-2 bg-white flex-shrink-0 relative">
        {/* @mention dropdown */}
        {mentionOpen && (
          <MentionDropdown members={members} filter={mentionFilter} onSelect={handleMentionSelect} />
        )}

        <div className="flex items-end gap-2">
          {/* File upload */}
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded flex-shrink-0 disabled:opacity-50"
            title="Attach file">
            <Paperclip className="h-4 w-4" />
          </button>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />

          {/* Text input */}
          <textarea ref={inputRef} value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown}
            placeholder={isPrivate ? 'Private message...' : 'Type a message...'}
            className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-300
                       max-h-[96px] min-h-[36px] leading-5"
            rows={1}
            style={{ height: 'auto', minHeight: '36px', maxHeight: '96px' }}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
          />

          {/* Send */}
          <button onClick={handleSend}
            disabled={!inputValue.trim() && attachments.length === 0}
            className="p-1.5 text-blue-500 hover:text-blue-600 rounded flex-shrink-0 disabled:text-gray-300 disabled:cursor-not-allowed"
            title="Send">
            <Send className="h-4 w-4" />
          </button>
        </div>

        {/* Toggle row */}
        <div className="flex items-center gap-2 mt-1.5">
          <button onClick={() => { setIsPrivate(!isPrivate); if (isPrivate) setRecipientIds([]); }}
            className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              isPrivate ? 'bg-amber-50 border-amber-200 text-amber-600' : 'border-gray-200 text-gray-400 hover:border-gray-300'
            }`}>
            <Lock className="h-3 w-3" /> {isPrivate ? 'Private' : 'Public'}
          </button>
          <button onClick={() => setIsUrgent(!isUrgent)}
            className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              isUrgent ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 text-gray-400 hover:border-gray-300'
            }`}>
            <Flag className="h-3 w-3" /> Urgent
          </button>
          {uploading && <span className="text-[10px] text-gray-400">Uploading...</span>}
        </div>
      </div>
    </div>
  );
}
